import type { AssetCatalogFilterState } from '@/features/admin-dashboard/types/assetCatalog';
import type { AssetMetadataFieldKey } from '@/features/assets/types/asset';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { cva } from 'class-variance-authority';

export const DURATION_MS_PER_SECOND = 1_000;
/** Placeholder rows on a cold catalog load — roughly a first page above the fold. */
export const ASSET_CATALOG_SKELETON_ROWS = 8;
export const BYTES_PER_MEGABYTE = 1_000_000;
export const assetCatalogSummaryVariants = cva(
  'flex shrink-0 items-baseline justify-end gap-2 text-muted-foreground text-xs transition-opacity',
  {
    variants: { stale: { true: 'opacity-60', false: null } },
    defaultVariants: { stale: false },
  },
);

export const assetCatalogViewportVariants = cva(
  'min-h-0 flex-1 space-y-3 overflow-auto transition-opacity',
  {
    variants: { stale: { true: 'opacity-60', false: null } },
    defaultVariants: { stale: false },
  },
);

export type AssetCatalogTableColumnId =
  | 'key'
  | 'organization'
  | 'status'
  | 'filename'
  | 'type'
  | 'duration'
  | 'size'
  | 'consent'
  | 'provenance'
  | 'deliveries'
  | 'open';

export const assetCatalogTableHeadVariants = cva('h-8 overflow-hidden p-1.5 text-xs', {
  variants: {
    column: {
      key: 'w-28',
      organization: 'w-32',
      status: 'w-24',
      filename: 'w-auto',
      type: 'w-16',
      duration: 'w-20',
      size: 'w-20',
      consent: 'w-24',
      provenance: 'w-24',
      deliveries: 'w-36',
      open: 'w-8 px-2',
    },
  },
});

export const assetCatalogTableCellVariants = cva('h-9 min-w-0 overflow-hidden p-1.5', {
  variants: {
    column: {
      key: 'w-28 truncate',
      organization: 'w-32 truncate',
      status: 'w-24',
      filename: 'w-auto whitespace-normal',
      type: 'w-16',
      duration: 'w-20',
      size: 'w-20',
      consent: 'w-24',
      provenance: 'w-24',
      deliveries: 'w-36',
      open: 'w-8 px-2',
    },
  },
});

export const INITIAL_ASSET_CATALOG_FILTERS: AssetCatalogFilterState = {
  organizationId: '',
  projectId: '',
  rejection: 'all',
  minDurationSeconds: '',
  maxDurationSeconds: '',
  minSizeMegabytes: '',
  maxSizeMegabytes: '',
  metadataValues: {},
  metadataPresence: {},
};

/**
 * Filter labels for the catalog's metadata fields. Exhaustive over
 * {@link AssetMetadataFieldKey} on purpose: it is the compile-time tripwire that
 * catches a key-set change that has not been carried through the UI.
 */
export const ASSET_METADATA_LABELS: Record<AssetMetadataFieldKey, MessageDescriptor> = {
  environment_id: msg`Venue`,
  operator_id: msg`Operator`,
  task_id: msg`Task`,
  device_id: msg`Device`,
  environment_l3: msg`Environment L3`,
  episode_task_description: msg`Episode task description`,
  episode_camera_shutter: msg`Episode camera shutter`,
  firmware_version: msg`Firmware version`,
};

export const ASSET_CATALOG_MEDIA_TYPE_LABELS = {
  video: msg`Video`,
  mcap: msg`MCAP`,
};

export const ASSET_CATALOG_UNKNOWN_MEDIA_TYPE_LABEL = msg`Unknown media type`;
